import React, { useState } from "react"

import { format } from "date-fns"
import {
  AlertCircle,
  Calendar,
  CheckCircle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock,
  Edit3,
  Loader2,
  MapPin,
  User,
  XCircle,
} from "lucide-react"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Textarea } from "@/components/ui/textarea"
import { INVALID_UK_TIME_ERROR } from "@/constants/sheriffOptions"
import {
  useApproveTimesheet,
  useRejectTimesheet,
  useReviewedTimesheets,
  useUnreviewedTimesheets,
} from "@/hooks/useTimesheets"
import { getErrorMessage } from "@/lib/getErrorMessage"
import { timesheetTimesError } from "@/lib/timesheetValidation"
import { ukFormat, ukInputToIso } from "@/lib/ukFormat"
import { timesheetsService } from "@/services/timesheetsService"
import { Timesheet, TimesheetFilters as TimesheetFiltersType } from "@/types/timesheet"

interface CompactTimesheetListProps {
  filters: TimesheetFiltersType
  searchTerm: string
  hideApproved?: boolean
}

const CompactTimesheetList: React.FC<CompactTimesheetListProps> = ({
  filters,
  searchTerm,
  hideApproved = false,
}) => {
  const [editedTimesheet, setEditedTimesheet] = useState<{
    clocked_in_time?: string
    clocked_out_time?: string
    break?: number
  }>({})

  // The API can't clear a clock-in or clock-out, and a time that doesn't exist in the UK can't be
  // saved, so both keep the old value rather than showing one that won't be saved.
  const handleActualTimeChange = (field: "clocked_in_time" | "clocked_out_time", value: string) => {
    if (!value) {
      toast.error("Clock-in and clock-out times can't be cleared, only changed.")
      return
    }
    const iso = ukInputToIso(value)
    if (!iso) {
      toast.error(INVALID_UK_TIME_ERROR)
      return
    }
    setEditedTimesheet((prev) => ({ ...prev, [field]: iso }))
  }

  const [rejectingTimesheet, setRejectingTimesheet] = useState<string | null>(null)
  const [rejectionReason, setRejectionReason] = useState("")
  const [showEditModal, setShowEditModal] = useState(false)
  const [timesheetToEdit, setTimesheetToEdit] = useState<Timesheet | null>(null)
  const [showApproveConfirm, setShowApproveConfirm] = useState(false)
  const [timesheetToApprove, setTimesheetToApprove] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [reviewedPage, setReviewedPage] = useState(1)
  const reviewedPageSize = 50
  const [selectedArea] = useState<string | "all">("all")
  const [expandedUnreviewedAreas, setExpandedUnreviewedAreas] = useState<Record<string, boolean>>(
    {},
  )
  const [expandedReviewedAreas, setExpandedReviewedAreas] = useState<Record<string, boolean>>({})

  // Fetch unreviewed timesheets (all Pending)
  const {
    data: unreviewedData = [],
    isLoading: unreviewedLoading,
    refetch: refetchUnreviewed,
  } = useUnreviewedTimesheets({
    date_from: filters.date_from,
    date_to: filters.date_to,
  })

  // Fetch reviewed timesheets with pagination
  const {
    data: reviewedResult,
    isLoading: reviewedLoading,
    refetch: refetchReviewed,
  } = useReviewedTimesheets({
    date_from: filters.date_from,
    date_to: filters.date_to,
    page: reviewedPage,
    pageSize: reviewedPageSize,
  })

  const reviewedData = reviewedResult?.data || []
  const reviewedCount = reviewedResult?.count || 0
  const totalReviewedPages = Math.ceil(reviewedCount / reviewedPageSize)

  const approveMutation = useApproveTimesheet()
  const rejectMutation = useRejectTimesheet()

  // Filter timesheets based on search
  const filterBySearch = (timesheets: Timesheet[]) => {
    if (searchTerm === "") return timesheets
    const searchLower = searchTerm.toLowerCase()
    return timesheets.filter(
      (timesheet) =>
        timesheet.person?.first_name?.toLowerCase().includes(searchLower) ||
        timesheet.person?.surname?.toLowerCase().includes(searchLower) ||
        timesheet.person?.person?.toLowerCase().includes(searchLower) ||
        timesheet.shift?.location?.toLowerCase().includes(searchLower),
    )
  }

  const filterByArea = (timesheets: Timesheet[]) => {
    if (selectedArea === "all") return timesheets
    return timesheets.filter((t) => (t.shift?.area?.trim() || "") === selectedArea)
  }

  const unreviewedTimesheets = filterByArea(filterBySearch(unreviewedData))
  const reviewedTimesheetsBase = hideApproved
    ? filterBySearch(reviewedData).filter((t) => t.status === "Denied")
    : filterBySearch(reviewedData)
  const reviewedTimesheets = filterByArea(reviewedTimesheetsBase)

  const groupedByArea = (timesheets: Timesheet[]) => {
    const groups: Record<string, Timesheet[]> = {}
    timesheets.forEach((t) => {
      const key = t.shift?.area?.trim() || "No area"
      if (!groups[key]) {
        groups[key] = []
      }
      groups[key].push(t)
    })
    return Object.entries(groups)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([area, rows]) => ({ area, rows }))
  }

  const toggleUnreviewedArea = (area: string) => {
    setExpandedUnreviewedAreas((prev) => ({
      ...prev,
      [area]: !prev[area],
    }))
  }

  const toggleReviewedArea = (area: string) => {
    setExpandedReviewedAreas((prev) => ({
      ...prev,
      [area]: !prev[area],
    }))
  }

  const refetch = async () => {
    await Promise.all([refetchUnreviewed(), refetchReviewed()])
  }

  const calculateHours = (clockIn?: string, clockOut?: string, breakMinutes?: number) => {
    if (!clockIn || !clockOut) return 0
    const diff = new Date(clockOut).getTime() - new Date(clockIn).getTime()
    const totalMinutes = diff / (1000 * 60)
    const workMinutes = totalMinutes - (breakMinutes || 0)
    return workMinutes / 60
  }

  const formatDuration = (hours: number) => {
    const wholeHours = Math.floor(hours)
    const minutes = Math.round((hours - wholeHours) * 60)
    return `${wholeHours}h${minutes > 0 ? ` ${minutes}m` : ""}`
  }

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      Pending: { label: "Pending", color: "bg-yellow-100 text-yellow-800" },
      Approved: { label: "Approved", color: "bg-green-100 text-green-800" },
      Denied: { label: "Rejected", color: "bg-red-100 text-red-800" },
      InProgress: { label: "In Progress", color: "bg-blue-100 text-blue-800" },
    }

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.Pending
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    )
  }

  const handleEdit = (timesheet: Timesheet) => {
    setTimesheetToEdit(timesheet)
    setEditedTimesheet({
      clocked_in_time: timesheet.clocked_in_time,
      clocked_out_time: timesheet.clocked_out_time,
      break: timesheet.break,
    })
    setShowEditModal(true)
  }

  const handleSave = async () => {
    if (!timesheetToEdit) return

    const timesError = timesheetTimesError(
      editedTimesheet.clocked_in_time,
      editedTimesheet.clocked_out_time,
    )
    if (timesError) {
      toast.error(timesError)
      return
    }

    setIsSaving(true)
    try {
      await timesheetsService.updateTimesheet({
        id: timesheetToEdit.id,
        clockedInTime: editedTimesheet.clocked_in_time,
        clockedOutTime: editedTimesheet.clocked_out_time,
        break: editedTimesheet.break,
      })

      await refetch()
      setShowEditModal(false)
      setTimesheetToEdit(null)
      setEditedTimesheet({})
    } catch (error) {
      console.error("Error saving timesheet:", error)
      toast.error(getErrorMessage(error, "Failed to save timesheet"))
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancel = () => {
    setShowEditModal(false)
    setTimesheetToEdit(null)
    setEditedTimesheet({})
  }

  const handleApprove = (timesheetId: string) => {
    setTimesheetToApprove(timesheetId)
    setShowApproveConfirm(true)
  }

  const confirmApprove = async () => {
    if (!timesheetToApprove) return
    try {
      await approveMutation.mutateAsync(timesheetToApprove)
      await refetch()
      setShowApproveConfirm(false)
      setTimesheetToApprove(null)
    } catch (error) {
      console.error("Error approving timesheet:", error)
      toast.error(getErrorMessage(error, "Failed to approve timesheet"))
    }
  }

  const cancelApprove = () => {
    setShowApproveConfirm(false)
    setTimesheetToApprove(null)
  }

  const handleReject = (timesheetId: string) => {
    setRejectingTimesheet(timesheetId)
    setRejectionReason("")
  }

  const confirmReject = async () => {
    if (!rejectingTimesheet || !rejectionReason.trim()) return
    try {
      await rejectMutation.mutateAsync({ id: rejectingTimesheet, reason: rejectionReason })
      await refetch()
      setRejectingTimesheet(null)
      setRejectionReason("")
    } catch (error) {
      console.error("Error rejecting timesheet:", error)
      toast.error(getErrorMessage(error, "Failed to reject timesheet"))
    }
  }

  const cancelReject = () => {
    setRejectingTimesheet(null)
    setRejectionReason("")
  }

  const renderTimesheetRow = (timesheet: Timesheet) => {
    const isUnreviewed = timesheet.status === "Pending"
    const hours = calculateHours(
      timesheet.clocked_in_time,
      timesheet.clocked_out_time,
      timesheet.break,
    )

    return (
      <TableRow key={timesheet.id} className="hover:bg-palette-jungleGreen000">
        <TableCell className="font-medium">
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-palette-jungleGreen400" />
            <span>
              {timesheet.person?.person ||
                `${timesheet.person?.first_name || ""} ${timesheet.person?.surname || ""}`.trim() ||
                "Unknown"}
            </span>
          </div>
        </TableCell>

        <TableCell>
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-palette-jungleGreen400" />
            <span className="text-sm">{timesheet.shift?.location || "-"}</span>
          </div>
        </TableCell>

        <TableCell className="text-sm">
          {timesheet.clocked_in_time ? ukFormat(timesheet.clocked_in_time, "MMM dd, yyyy") : "-"}
        </TableCell>

        <TableCell>
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1">
              <Clock className="h-3 w-3 text-palette-jungleGreen400" />
              <span className="text-sm">
                {timesheet.clocked_in_time
                  ? ukFormat(timesheet.clocked_in_time, "HH:mm")
                  : "Not set"}
              </span>
            </div>
            {timesheet.shift?.start_time && (
              <span className="text-xs text-palette-jungleGreen500">
                Planned: {ukFormat(timesheet.shift.start_time, "HH:mm")}
              </span>
            )}
          </div>
        </TableCell>

        <TableCell>
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1">
              <Clock className="h-3 w-3 text-palette-jungleGreen400" />
              <span className="text-sm">
                {timesheet.clocked_out_time
                  ? ukFormat(timesheet.clocked_out_time, "HH:mm")
                  : "Not set"}
              </span>
            </div>
            {timesheet.shift?.end_time && (
              <span className="text-xs text-palette-jungleGreen500">
                Planned: {ukFormat(timesheet.shift.end_time, "HH:mm")}
              </span>
            )}
          </div>
        </TableCell>

        <TableCell className="text-sm font-medium">{formatDuration(hours)}</TableCell>

        <TableCell>
          <div className="max-w-32 truncate text-sm text-palette-jungleGreen600">
            {timesheet.shift?.notes || "No notes"}
          </div>
        </TableCell>

        <TableCell>{getStatusBadge(timesheet.status)}</TableCell>

        <TableCell>
          {isUnreviewed ? (
            <div className="flex gap-2">
              <Button
                size="default"
                variant="outline"
                onClick={() => handleEdit(timesheet)}
                className="h-9 px-3"
              >
                <Edit3 className="h-4 w-4 mr-1" />
                Edit
              </Button>
              <Button
                size="default"
                onClick={() => handleApprove(timesheet.id)}
                className="h-9 px-3 bg-green-500 hover:bg-green-600"
                disabled={approveMutation.isPending}
              >
                <CheckCircle className="h-4 w-4 mr-1" />
                Approve
              </Button>
              <Button
                size="default"
                variant="destructive"
                onClick={() => handleReject(timesheet.id)}
                className="h-9 px-3"
                disabled={rejectMutation.isPending}
              >
                <XCircle className="h-4 w-4 mr-1" />
                Reject
              </Button>
            </div>
          ) : (
            <div className="text-xs text-palette-jungleGreen500">
              {timesheet.last_edited_by && <div>By {timesheet.last_edited_by}</div>}
              {timesheet.updated_at && (
                <div>{format(new Date(timesheet.updated_at), "MMM dd, HH:mm")}</div>
              )}
            </div>
          )}
        </TableCell>
      </TableRow>
    )
  }

  return (
    <div className="space-y-6">
      {/* Unreviewed Timesheets */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <AlertCircle className="h-5 w-5 text-yellow-500" />
          <h3 className="text-lg font-semibold">Unreviewed Timesheets</h3>
          <span className="px-2 py-1 bg-palette-jungleGreen100 rounded-full text-sm font-medium">
            {unreviewedTimesheets.length}
          </span>
        </div>

        {unreviewedLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-palette-jungleGreen400" />
            <span className="ml-2 text-palette-jungleGreen600">
              Loading unreviewed timesheets...
            </span>
          </div>
        ) : unreviewedTimesheets.length === 0 ? (
          <div className="text-center py-8 text-palette-jungleGreen500">
            No unreviewed timesheets found
          </div>
        ) : (
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Person</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Clock In</TableHead>
                  <TableHead>Clock Out</TableHead>
                  <TableHead>Hours</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {groupedByArea(unreviewedTimesheets).map((group) => (
                  <React.Fragment key={group.area}>
                    <TableRow className="bg-palette-jungleGreen000">
                      <TableCell colSpan={9}>
                        <button
                          type="button"
                          className="flex items-center justify-between w-full text-left"
                          onClick={() => toggleUnreviewedArea(group.area)}
                        >
                          <div className="flex items-center gap-2">
                            {expandedUnreviewedAreas[group.area] ? (
                              <ChevronDown className="h-4 w-4 text-palette-jungleGreen500" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-palette-jungleGreen500" />
                            )}
                            <span className="font-medium text-palette-jungleGreen800">
                              {group.area}
                            </span>
                          </div>
                          <span className="text-xs text-palette-jungleGreen500">
                            {group.rows.length} timesheet
                            {group.rows.length === 1 ? "" : "s"}
                          </span>
                        </button>
                      </TableCell>
                    </TableRow>
                    {expandedUnreviewedAreas[group.area]
                      ? group.rows.map((t) => renderTimesheetRow(t))
                      : null}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Reviewed Timesheets */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-500" />
            <h3 className="text-lg font-semibold">
              All Timesheets (Reviewed, Denied, In Progress)
            </h3>
            <span className="px-2 py-1 bg-palette-jungleGreen100 rounded-full text-sm font-medium">
              Showing {reviewedTimesheets.length} of {reviewedCount}
            </span>
          </div>

          {/* Pagination Controls */}
          {totalReviewedPages > 1 && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setReviewedPage((p) => Math.max(1, p - 1))}
                disabled={reviewedPage === 1 || reviewedLoading}
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <span className="text-sm text-palette-jungleGreen600">
                Page {reviewedPage} of {totalReviewedPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setReviewedPage((p) => Math.min(totalReviewedPages, p + 1))}
                disabled={reviewedPage === totalReviewedPages || reviewedLoading}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>

        {reviewedLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-palette-jungleGreen400" />
            <span className="ml-2 text-palette-jungleGreen600">Loading timesheets...</span>
          </div>
        ) : reviewedTimesheets.length === 0 ? (
          <div className="text-center py-8 text-palette-jungleGreen500">No timesheets found</div>
        ) : (
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Person</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Clock In</TableHead>
                  <TableHead>Clock Out</TableHead>
                  <TableHead>Hours</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Reviewed</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {groupedByArea(reviewedTimesheets).map((group) => (
                  <React.Fragment key={group.area}>
                    <TableRow className="bg-palette-jungleGreen000">
                      <TableCell colSpan={9}>
                        <button
                          type="button"
                          className="flex items-center justify-between w-full text-left"
                          onClick={() => toggleReviewedArea(group.area)}
                        >
                          <div className="flex items-center gap-2">
                            {expandedReviewedAreas[group.area] ? (
                              <ChevronDown className="h-4 w-4 text-palette-jungleGreen500" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-palette-jungleGreen500" />
                            )}
                            <span className="font-medium text-palette-jungleGreen800">
                              {group.area}
                            </span>
                          </div>
                          <span className="text-xs text-palette-jungleGreen500">
                            {group.rows.length} timesheet
                            {group.rows.length === 1 ? "" : "s"}
                          </span>
                        </button>
                      </TableCell>
                    </TableRow>
                    {expandedReviewedAreas[group.area]
                      ? group.rows.map((t) => renderTimesheetRow(t))
                      : null}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Edit Timesheet Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <Calendar className="h-6 w-6 text-blue-500" />
              Edit Timesheet Times
            </DialogTitle>
          </DialogHeader>

          {timesheetToEdit && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Employee</Label>
                <div className="p-2 bg-palette-jungleGreen000 rounded text-sm">
                  {timesheetToEdit.person?.person ||
                    `${timesheetToEdit.person?.first_name || ""} ${timesheetToEdit.person?.surname || ""}`.trim() ||
                    "Unknown"}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Date</Label>
                <div className="p-2 bg-palette-jungleGreen000 rounded text-sm">
                  {timesheetToEdit.clocked_in_time
                    ? ukFormat(timesheetToEdit.clocked_in_time, "MMM dd, yyyy")
                    : "-"}
                </div>
              </div>

              {/* Planned Times */}
              {(timesheetToEdit.shift?.start_time || timesheetToEdit.shift?.end_time) && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Planned Start Time</Label>
                    <div className="p-2 bg-palette-jungleGreen000 rounded text-sm">
                      {timesheetToEdit.shift?.start_time
                        ? ukFormat(timesheetToEdit.shift.start_time, "HH:mm")
                        : "Not set"}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Planned End Time</Label>
                    <div className="p-2 bg-palette-jungleGreen000 rounded text-sm">
                      {timesheetToEdit.shift?.end_time
                        ? ukFormat(timesheetToEdit.shift.end_time, "HH:mm")
                        : "Not set"}
                    </div>
                  </div>
                </div>
              )}

              {/* Actual Times */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Clock In Time</Label>
                  <Input
                    type="datetime-local"
                    value={
                      editedTimesheet.clocked_in_time
                        ? ukFormat(editedTimesheet.clocked_in_time, "yyyy-MM-dd'T'HH:mm")
                        : ""
                    }
                    onChange={(e) => handleActualTimeChange("clocked_in_time", e.target.value)}
                    className="[&::-webkit-calendar-picker-indicator]:opacity-100 [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Clock Out Time</Label>
                  <Input
                    type="datetime-local"
                    value={
                      editedTimesheet.clocked_out_time
                        ? ukFormat(editedTimesheet.clocked_out_time, "yyyy-MM-dd'T'HH:mm")
                        : ""
                    }
                    onChange={(e) => handleActualTimeChange("clocked_out_time", e.target.value)}
                    className="[&::-webkit-calendar-picker-indicator]:opacity-100 [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                  />
                </div>
              </div>

              {/* Break Time and Hours */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Break Time (Minutes)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    value={editedTimesheet.break || 0}
                    onChange={(e) =>
                      setEditedTimesheet({
                        ...editedTimesheet,
                        break: parseInt(e.target.value) || 0,
                      })
                    }
                    placeholder="Break time in minutes"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Hours</Label>
                  <div className="p-2 bg-palette-jungleGreen000 rounded text-sm">
                    {formatDuration(
                      calculateHours(
                        editedTimesheet.clocked_in_time,
                        editedTimesheet.clocked_out_time,
                        editedTimesheet.break,
                      ),
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={handleCancel} disabled={isSaving}>
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={isSaving}>
                  {isSaving ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    "Save Changes"
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Approval Confirmation Modal */}
      <Dialog open={showApproveConfirm} onOpenChange={setShowApproveConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <CheckCircle className="h-6 w-6 text-green-500" />
              Confirm Approval
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-palette-jungleGreen600">
              Are you sure you want to approve this timesheet? This action cannot be undone.
            </p>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={cancelApprove}
                disabled={approveMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                onClick={confirmApprove}
                className="bg-green-500 hover:bg-green-600"
                disabled={approveMutation.isPending}
              >
                {approveMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Approving...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Approve Timesheet
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Rejection Modal */}
      <Dialog open={!!rejectingTimesheet} onOpenChange={(open) => !open && cancelReject()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <XCircle className="h-6 w-6 text-red-500" />
              Reject Timesheet
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="rejection-reason">Rejection Reason</Label>
              <Textarea
                id="rejection-reason"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Please provide a reason for rejection..."
                rows={4}
              />
              <p className="text-xs text-palette-jungleGreen500">
                Note: Rejection reason will be logged but not stored in the timesheet record.
              </p>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={cancelReject} disabled={rejectMutation.isPending}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={confirmReject}
                disabled={!rejectionReason.trim() || rejectMutation.isPending}
              >
                {rejectMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Rejecting...
                  </>
                ) : (
                  <>
                    <XCircle className="h-4 w-4 mr-2" />
                    Reject Timesheet
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default CompactTimesheetList
